import React, { useState, useCallback } from 'react';
import { Modal } from '@/components/ui/modal';
import Button from '@/components/ui/button/Button';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { withFormProtection } from '@/components/form/withFormProtection';
import { useToast } from '@/components/ui/toast/Toast';

interface RentingDateTimeEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  initialData?: FormData;
  currentData?: FormData;
  onDataChange?: (data: FormData) => void;
  type: 'start' | 'end';
  currentDate: string;
  currentTime: string;
}

interface FormData {
  date: string;
  time: string;
}

const RentingDateTimeEditModal = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, RentingDateTimeEditModalProps>(({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  onDataChange,
  type,
  currentDate,
  currentTime,
}, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [date, setDate] = useState(initialData?.date || currentDate || '');
  const [time, setTime] = useState(initialData?.time || currentTime || '');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Обновляем состояние когда меняются props
  React.useEffect(() => {
    setDate(initialData?.date || currentDate || '');
    setTime(initialData?.time || currentTime || '');
  }, [initialData, currentDate, currentTime]);

  // Notify parent about form data changes
  const handleFormDataChange = useCallback((newDate: string, newTime: string) => {
    onDataChange?.({
      date: newDate,
      time: newTime,
    });
  }, [onDataChange]);

  // Update form data when date changes
  const handleDateChange = (selectedDate: Date | null) => {
    const newDate = selectedDate ? selectedDate.toISOString().split('T')[0] : '';
    setDate(newDate);
    handleFormDataChange(newDate, time);
    validateForm(newDate, time);
  };

  // Update form data when time changes
  const handleTimeChange = (value: string) => {
    setTime(value);
    handleFormDataChange(date, value);
    validateForm(date, value);
  };

  const validateForm = (currentDate: string, currentTime: string): boolean => {
    if (!currentDate) {
      setError(t('rentings.datetime_edit.errors.date_required'));
      return false;
    }
    if (!currentTime) {
      setError(t('rentings.datetime_edit.errors.time_required'));
      return false;
    }

    // Validate that the new date/time is not in the past
    const newDateTime = new Date(`${currentDate}T${currentTime}`);
    const now = new Date();

    if (newDateTime <= now) {
      setError(t('rentings.datetime_edit.errors.future_datetime_required'));
      return false;
    }

    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm(date, time)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        date,
        time,
      });
      onClose();
      toast.success(t('rentings.datetime_edit.update_success'));
    } catch (error) {
      toast.error(t('rentings.datetime_edit.update_error' + `<br/><br/>${error}`));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateChangeRaw = (value: string) => {
    const [day, month, year] = value.split('.');
    if (day && month && year) {
      const date = new Date(`${year}-${month}-${day}`);
      if (date instanceof Date && !isNaN(date.getTime())) {
        handleDateChange(date);
      }
    }
  };

  const getDatePickerClassName = (hasError: boolean | undefined) => {
    return `w-full rounded-lg ${hasError ? 'border-error-500 dark:border-error-500' : 'border-gray-300 dark:border-gray-700'} bg-transparent py-[9px] px-4 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:bg-form-input dark:focus:border-primary`;
  };

  const title = type === 'start'
    ? t('rentings.datetime_edit.title.edit_start_datetime')
    : t('rentings.datetime_edit.title.edit_end_datetime');

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="max-w-[500px] p-6"
      >
        <style>
          {`
            .react-datepicker-wrapper {
              width: 100%;
            }

            .react-datepicker-popper {
              z-index: 20;
            }

            .react-datepicker__month-container {
              float: none !important;
              min-height: 310px !important;
            }

            /* Стили для мобильных устройств */
            @media (max-width: 767px) {
              .react-datepicker-popper {
                position: fixed !important;
                top: auto !important;
                bottom: 0 !important;
                left: 0 !important;
                right: 0 !important;
                transform: none !important;
                margin: 1rem !important;
                width: calc(100% - 2rem) !important;
              }

              .react-datepicker {
                width: 100% !important;
              }

              .react-datepicker__month-container {
                width: 100% !important;
                float: none !important;
              }

              .react-datepicker__day {
                width: calc((100% - 2.8rem) / 7) !important;
                line-height: 2.5rem !important;
                margin: 0.2rem !important;
              }

              .react-datepicker__day-name {
                width: calc((100% - 2.8rem) / 7) !important;
                margin: 0.2rem !important;
              }
            }

            /* Стили для десктопа */
            @media (min-width: 768px) {
              .react-datepicker-wrapper {
                position: relative;
              }

              .react-datepicker-popper {
                z-index: 20;
                margin-top: -10rem !important;
              }

              .react-datepicker-popper[data-placement^='bottom-start'] {
                margin-left: 0rem !important;
              }

              .react-datepicker-popper[data-placement^='bottom-end'] {
                margin-right: 0.5rem !important;
              }
            }

            .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
              display: none;
            }

            .react-datepicker {
              font-family: inherit;
              border: 1px solid #e5e7eb !important;
              border-radius: 1rem;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
              font-size: 0.875rem;
            }

            .react-datepicker__header {
              background-color: transparent;
              border-bottom: 1px solid #e5e7eb;
              border-radius: 1rem 1rem 0 0;
              padding: 0.75rem 0;
            }

            .react-datepicker__current-month {
              font-weight: 600;
              font-size: 0.875rem;
              color: #1f2937;
              margin-bottom: 0.5rem;
            }

            .react-datepicker__day-name {
              color: #6b7280;
              font-weight: 500;
              font-size: 0.75rem;
              width: 2rem;
              margin: 0.2rem;
            }

            .react-datepicker__day {
              color: #1f2937;
              border-radius: 0.5rem;
              margin: 0.2rem;
              width: 2rem;
              line-height: 2rem;
              font-size: 0.875rem;
            }

            .react-datepicker__day:hover {
              background-color: #f3f4f6;
              border-radius: 0.5rem;
            }

            .react-datepicker__day--selected,
            .react-datepicker__day--keyboard-selected {
              background-color: #3b82f6 !important;
              border-radius: 0.5rem;
              color: white !important;
            }

            .react-datepicker__day--disabled {
              color: #9ca3af;
            }

            .react-datepicker__navigation {
              top: 0.75rem;
            }

            .react-datepicker__navigation-icon::before {
              border-color: #6b7280;
              border-width: 2px 2px 0 0;
              height: 8px;
              width: 8px;
            }

            .dark .react-datepicker {
              background-color: #1e293b;
              border-color: #334155 !important;
            }

            .dark .react-datepicker__header {
              background-color: #1e293b;
              border-color: #334155;
            }

            .dark .react-datepicker__current-month {
              color: #e5e7eb;
            }

            .dark .react-datepicker__day-name {
              color: #9ca3af;
            }

            .dark .react-datepicker__day {
              color: #e5e7eb;
            }

            .dark .react-datepicker__day:hover {
              background-color: #334155;
            }

            .dark .react-datepicker__day--disabled {
              color: #4b5563;
            }

            .dark .react-datepicker__navigation-icon::before {
              border-color: #9ca3af;
            }
          `}
        </style>

        <div className="px-6 py-4">
          <h5 className="text-xl font-semibold text-gray-800 dark:text-white/90 lg:text-2xl">
            {title}
          </h5>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('rentings.datetime_edit.description')}
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <div>
              <Label>
                {t('rentings.datetime_edit.fields.date')}
                <span className="text-danger ml-1">*</span>
              </Label>
              <ReactDatePicker
                selected={date ? new Date(date) : null}
                onChange={handleDateChange}
                dateFormat="dd.MM.yyyy"
                className={getDatePickerClassName(!!error)}
                minDate={new Date()}
                onChangeRaw={(e: any) => {
                  if (e?.target?.value) {
                    handleDateChangeRaw(e.target.value);
                  }
                }}
              />
            </div>

            <div>
              <Label>
                {t('rentings.datetime_edit.fields.time')}
                <span className="text-danger ml-1">*</span>
              </Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => handleTimeChange(e.target.value)}
                error={!!error}
                aria-required="true"
              />
            </div>

            {error && (
              <div className="text-danger text-sm">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="flex items-center gap-3 sm:justify-end">
            <Button variant="outline" size="sm" onClick={onClose}>
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('rentings.datetime_edit.buttons.updating') : t('rentings.datetime_edit.buttons.update')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
});

RentingDateTimeEditModal.displayName = 'RentingDateTimeEditModal';

const ProtectedRentingDateTimeEditModal = withFormProtection(RentingDateTimeEditModal);

interface RentingDateTimeEditModalWrapperProps extends Omit<RentingDateTimeEditModalProps, 'currentData' | 'onDataChange' | 'initialData'> {
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean }>;
}

const RentingDateTimeEditModalWrapper = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, RentingDateTimeEditModalWrapperProps>((props, ref) => {
  const initialData = {
    date: props.currentDate || '',
    time: props.currentTime || '',
  };

  const [currentData, setCurrentData] = useState<FormData>(initialData);

  // Обновляем currentData когда меняются props
  React.useEffect(() => {
    setCurrentData({
      date: props.currentDate || '',
      time: props.currentTime || '',
    });
  }, [props.currentDate, props.currentTime]);

  const handleDataChange = useCallback((data: FormData) => {
    setCurrentData(data);
  }, []);

  return (
    <ProtectedRentingDateTimeEditModal
      {...props}
      ref={ref}
      initialData={initialData}
      currentData={currentData}
      onDataChange={handleDataChange}
    />
  );
});

RentingDateTimeEditModalWrapper.displayName = 'RentingDateTimeEditModalWrapper';

export default RentingDateTimeEditModalWrapper;
